import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

const CONCURRENCY = 5

export default class extends Controller {
  static targets = ["input", "list", "rowTemplate", "zone"]
  static classes = ["dragging"]
  static values = { url: String }

  connect() {
    this.queue = []
    this.running = 0
    this.filesByRow = new Map()
    this.failedRows = new Set()
  }

  highlight(event) {
    event.preventDefault()
    this.zoneTarget.classList.add(...this.draggingClasses)
  }

  unhighlight() {
    this.zoneTarget.classList.remove(...this.draggingClasses)
  }

  add() {
    Array.from(this.inputTarget.files).forEach((file) => this.enqueue(file))
    this.inputTarget.value = ""
    this.pump()
  }

  enqueue(file) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)
    this.slot(row, "name").textContent = file.name
    this.slot(row, "size").textContent = this.humanSize(file.size)
    this.listTarget.appendChild(row)
    this.filesByRow.set(row, file)
    this.queue.push(row)
    this.announce()
  }

  pump() {
    while (this.running < CONCURRENCY && this.queue.length > 0) {
      this.upload(this.queue.shift())
    }
  }

  upload(row) {
    this.running += 1
    this.failedRows.delete(row)
    this.announce()
    this.slot(row, "progress").classList.remove("hidden")
    this.slot(row, "retry").classList.add("hidden")
    this.slot(row, "error").classList.add("hidden")

    const upload = new DirectUpload(this.filesByRow.get(row), this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => this.setProgress(row, (event.loaded / event.total) * 100))
      }
    })

    upload.create((error, blob) => {
      this.running -= 1
      if (error) {
        this.markFailed(row)
      } else {
        this.markUploaded(row, blob)
      }
      this.announce()
      this.pump()
    })
  }

  markUploaded(row, blob) {
    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = this.inputTarget.name
    hidden.value = blob.signed_id
    row.appendChild(hidden)
    this.slot(row, "progress").classList.add("hidden")
    this.dispatch("uploaded", { detail: { filename: blob.filename } })
  }

  markFailed(row) {
    this.failedRows.add(row)
    this.slot(row, "progress").classList.add("hidden")
    this.slot(row, "retry").classList.remove("hidden")
    this.slot(row, "error").classList.remove("hidden")
  }

  retry(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("li")
    this.queue.push(row)
    this.pump()
  }

  remove(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("li")
    const filename = this.slot(row, "name").textContent
    this.queue = this.queue.filter((queued) => queued !== row)
    this.filesByRow.delete(row)
    this.failedRows.delete(row)
    row.remove()
    this.dispatch("removed", { detail: { filename } })
    this.announce()
  }

  discardPending(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("li")
    const filename = this.slot(row, "name").textContent
    row.remove()
    this.dispatch("removed", { detail: { filename } })
  }

  setProgress(row, progress) {
    this.slot(row, "progressBar").style.setProperty("width", `${progress}%`)
  }

  announce() {
    this.dispatch("pending", { detail: { count: this.queue.length + this.running, failed: this.failedRows.size } })
  }

  slot(row, name) {
    return row.querySelector(`[data-upload-dropzone-slot='${name}']`)
  }

  humanSize(bytes) {
    const units = ["byte", "kilobyte", "megabyte", "gigabyte"]
    let value = bytes
    let unitIndex = 0
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024
      unitIndex += 1
    }
    // Aligned on number_to_human_size, which the server renders the already
    // attached files with: « 74 octets », then « 1,2 ko ».
    return new Intl.NumberFormat(document.documentElement.lang, {
      style: "unit",
      unit: units[unitIndex],
      unitDisplay: unitIndex === 0 ? "long" : "short",
      maximumFractionDigits: unitIndex === 0 ? 0 : 1
    }).format(value)
  }
}
